import { GlobalFacility } from '@/graphql/generated/schemas'

// Mirrors the generated GraphQL nullability rather than an idealised shape:
// every hop here really can come back null, and web-app compiles with
// `strict: false`, so an over-tight type would silently protect nothing.
interface FacilityWithFloorIdsI {
  defaultFloorId?: string | null
  floors?: {
    edges?: Array<{ node?: { id: string } | null } | null> | null
  } | null
}

export const uuid = () =>
  (performance.now().toString(36) + Math.random().toString(36)).replace(
    /\./g,
    ''
  )
export const getMajorMinorVersion = (version: string): string => {
  const isMajorMinorPatchVersion = version?.split('.').length - 1 === 2
  if (isMajorMinorPatchVersion) {
    return version?.substring(0, version?.lastIndexOf('.'))
  }
  return version
}

/**
 * The floor a facility "opens to" — its configured default, else its first
 * floor. Callers must handle `undefined`: a facility with no floors has
 * nowhere to navigate (ENG-32089).
 *
 * Every hop is optional-chained because both fields are optional on the type
 * and `floors` is nullable in the schema — the previous `facility.floors.edges`
 * threw a TypeError for any facility whose `defaultFloorId` was unset and
 * whose `floors` was absent from the caller's selection set.
 */
export const getDefaultOrFirstFloorId = (
  facility?: FacilityWithFloorIdsI | null
): string | undefined =>
  // `||`, not `??`: an empty-string defaultFloorId is not a usable floor and
  // must fall through to the first floor, otherwise it builds a floor-plan
  // link with no id.
  facility?.defaultFloorId || facility?.floors?.edges?.[0]?.node?.id

// Ensure that all cases where handled in switch-case
// Reference: <https://www.meticulous.ai/blog/safer-exhaustive-switch-statements-in-typescript>
export const exhaustiveCheck = (switchArg: never) => {
  const exhaustiveCheck: never = switchArg
  throw new Error(`Unexpected type: ${exhaustiveCheck}`)
}

export const isValidTimestamp = (str: string) => !isNaN(Date.parse(str))

/**
 * Shuffles an array using the Fisher-Yates algorithm.
 *
 * @param array - The array to shuffle
 * @returns A new shuffled array
 */
export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

export type GlobalMapProperties = {
  id: string
  shortName: string
  defaultFloorId: string
  lat: number
  lon: number
  activeIncidentCount: number
}

export type MapOfStringToNumber = Record<string, number>

/**
 * Takes a list of Facilities (`globalFacilities`) & a map of Facilities with Incidents (`mapOfFacilitiesToIncidents`)
 * and merges them into one consumable object for usage within map.
 *
 * These are two distinctly different objects as we can load the list of all Facilities once, and then continue to
 * refresh Incidents after the fact w/o as much strain on DB
 *
 * @param globalFacilities
 * @param mapOfFacilitiesToIncidents
 */
export const mergeCountsIntoFacilities = (
  globalFacilities: GlobalFacility[],
  mapOfFacilitiesToIncidents: MapOfStringToNumber
): GlobalMapProperties[] => {
  if (globalFacilities === undefined) {
    return []
  }

  return globalFacilities.map((el: GlobalFacility) => {
    const activeIncidentCount: number = mapOfFacilitiesToIncidents?.[el.id] ?? 0

    const globalMapProperties: GlobalMapProperties = {
      id: el.id,
      shortName: el.shortName,
      defaultFloorId: el.defaultFloorId,
      lat: el.lat,
      lon: el.lon,
      activeIncidentCount: activeIncidentCount,
    }
    return globalMapProperties
  })
}
